import { Fragment, useState } from 'react';
import {
  facilityOrganization,
  facilityOrganizationSave,
  governedSettings,
  governedStatus,
  orgProfile,
  orgProfileSave,
  tenantRename,
  tenantSettingsSave,
  type ApiError,
  type FacilityOrganization,
  type FacilityOrganizationBody,
  type OrgProfile,
  type OrgProfileResponse,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { Skeleton } from '../../ui/states';
import { ErrLine, I, btn, hhmm, pre, sx, useApi, useGuarded, usePage , LoadError } from './kit';
import { BuildingContactConsent } from './AlertPhoneEditor';
import { US_STATES, US_STATE_CODES, US_ZONES, otherZones } from './places';


type FieldType = 'text' | 'copy' | 'select' | 'tz' | 'state';
/** [label, type, key, hint] as the design lists them (ORG, L21827). */
type FieldDef = [string, FieldType, string, string];

const PLANS = ['Not set', 'Pilot', 'Standard', 'Enterprise'];
const REGIONS = ['US · Virginia', 'US · Oregon', 'EU · Frankfurt', 'EU · Dublin'];
const BAAS = ['None', 'Sent', 'Signed', 'Countersigned'];
const CYCLES: Record<string, string[]> = { plan: PLANS, region: REGIONS, baa: BAAS };
/** The State dropdown's value that opens a text box for a place outside the US. */
const OUTSIDE = '__outside__';

const LOOKUP: [string, FieldDef[]] = ['What we can’t look up', [
  ['Ownership', 'text', 'own', 'who owns the building, e.g. nonprofit, chain, private'],
  ['Units', 'text', 'units', 'how many units (apartments or rooms), e.g. 48'],
]];
const GROUPS_BEFORE: [string, FieldDef[]][] = [
  ['Identity', [['Organization name', 'text', 'name', 'the name staff and families see'], ['Legal name', 'text', 'legal', 'as registered'], ['Organization id', 'copy', 'tenant', 'set by Aithora, cannot be changed'], ['Plan / tier', 'select', 'plan', '']]],
  ['Address', [['Street', 'text', 'street', ''], ['Region', 'select', 'region', 'where resident data rests'], ['City', 'text', 'city', ''], ['State', 'state', 'state', ''], ['ZIP', 'text', 'zip', '']]],
  ['Provider identifiers', [
    ['NPI', 'text', 'npi', ''],
    ['CCN', 'text', 'ccn', ''],
    ['Linked facility id', 'text', 'facid', ''],
    ['Licensed beds', 'text', 'beds', ''],
    ['Time zone', 'tz', 'tz', 'sets quiet hours, med-pass windows and deadlines'],
  ]],
];
const GROUPS_AFTER: [string, FieldDef[]][] = [
  ['Billing contact', [['Name', 'text', 'bname', 'who receives the invoices'], ['Email', 'text', 'bemail', 'invoices and dunning go here']]],
  ['Business associate agreement', [['BAA status', 'select', 'baa', 'HIPAA requires this before any PHI flows'], ['Signed date', 'text', 'bdate', 'YYYY-MM-DD, kept only when the status is Signed']]],
];

const FIELD = 'width:100%;height:36px;padding:0 12px;border:1px solid rgba(34,39,46,.14);border-radius:8px;background:#F7F3EA;font-size:16px;color:#22272E;font-family:inherit;box-sizing:border-box;';
/** A field the catalog filled reads as read only: no fill, a dashed edge. */
const FILLED = 'background:transparent;border-style:dashed;color:#3C454F;';

/** The note on a field the CMS catalog fills. */
const CATALOG_NOTE = 'Filled from the CMS catalog';
const TYPE_OR_LINK = 'Type it, or set a Linked facility id to fill it from the CMS catalog';
/** Aithora's lens: what GET /api/settings/org-profile returns as `sourced` (server.ts resolveFacilitySource). */
const OPERATOR_CATALOG = new Set(['npi', 'ccn', 'street', 'city', 'state', 'zip']);
/** A building's lens: the catalog row matched by its CCN (v3/settings.ts organizationView). */
const FACILITY_CATALOG = new Set(['street', 'city', 'state', 'zip']);

/** Aithora's lens: what each field takes, or that the catalog filled it. */
function operatorHint(k: string, linked: boolean, facid: string, facidEdited: boolean): string {
  if (linked && OPERATOR_CATALOG.has(k)) return CATALOG_NOTE;
  if (k === 'facid' && facidEdited) return 'Save changes to look this id up in the CMS catalog';
  switch (k) {
    case 'npi': return '10 digits. ' + TYPE_OR_LINK;
    case 'ccn': return '6 characters from CMS. ' + TYPE_OR_LINK;
    case 'street': case 'city': case 'state': return TYPE_OR_LINK;
    case 'zip': return '5 digits. ' + TYPE_OR_LINK;
    case 'facid':
      if (linked) return 'Linked: NPI, CCN and address come from the CMS catalog';
      if (facid.trim()) return 'No CMS catalog entry matches this id, so NPI, CCN and address stay as typed';
      return 'Enter a CMS catalog id, CCN or NPI to fill NPI, CCN and address automatically';
    case 'beds': return 'Not kept for the whole organization; each building sets its own';
    default: return '';
  }
}

/** A building's lens: the address and catalog link come from its CCN; beds can be typed over. */
function facilityHint(k: string, o: FacilityOrganization): string {
  const linked = !!o.linkedFacilityId;
  if (FACILITY_CATALOG.has(k)) return linked ? CATALOG_NOTE : 'Fills from the CMS catalog once this building’s CCN matches it';
  switch (k) {
    case 'npi': return '10 digits';
    case 'ccn': return 'CMS certification number';
    case 'facid': return linked ? 'Found in the CMS catalog by this building’s CCN' : 'No CMS catalog entry matches this building’s CCN yet';
    case 'beds': return o.licensedBedsSource === 'cms_catalog' ? 'Filled from the CMS catalog. Type your own count to replace it' : 'how many licensed beds, e.g. 60';
    case 'bdate': return 'Recorded by Aithora when the agreement is signed';
    default: return '';
  }
}

/** "2026-08-14" as "Aug 14, 2026" (design: Settings › BAA signed date). Anything else is shown as it is. */
export function longDate(v: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v.trim());
  if (!m) return v;
  const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const mon = MON[Number(m[2]) - 1];
  return mon ? `${mon} ${Number(m[3])}, ${m[1]}` : v;
}

const planLabel = (p: string | null | undefined) => (p ? PLANS.find((x) => x.toLowerCase() === p.toLowerCase()) ?? p : 'Not set');
const baaLabel = (b: string | null | undefined) => (b ? BAAS.find((x) => x.toLowerCase() === b.toLowerCase()) ?? b : 'None');

/** The governed organization's values (Aithora's lens), by field key. */
function fromProfile(p: OrgProfile, tenant: { id: string; name: string } | null, region: string | undefined, src: OrgProfileResponse['sourced']): Record<string, string> {
  // With a linked catalog row, NPI, CCN and address are the catalog's (read only here).
  const c = src ?? p;
  return {
    name: tenant?.name ?? '', legal: p.legalName, tenant: tenant?.id ?? '', plan: planLabel(p.plan), street: c.street, region: region || 'Not set', city: c.city,
    state: c.state, zip: c.zip, npi: c.npi, ccn: c.ccn, facid: p.facilityId, beds: p.licensedBeds ?? '', tz: p.timezone,
    own: p.ownership ?? '', units: p.units ?? '', logo: p.logoUrl ?? '', bname: p.billingContactName, bemail: p.billingContactEmail, baa: baaLabel(p.baaStatus), bdate: p.baaSignedDate,
  };
}

/** A building's values (the Facility Admin's lens). */
function fromFacility(o: FacilityOrganization): Record<string, string> {
  const s = (v: unknown) => (v === null || v === undefined ? '' : String(v));
  return {
    name: s(o.name), legal: s(o.legalName), tenant: o.id, plan: planLabel(o.plan), street: s(o.street), region: o.region || 'Not set', city: s(o.city),
    state: s(o.state), zip: s(o.zip), npi: s(o.npi), ccn: s(o.ccn), facid: s(o.linkedFacilityId), beds: s(o.licensedBeds), tz: s(o.timezone),
    own: s(o.ownership), units: s(o.units), bname: s(o.billingContactName), bemail: s(o.billingContactEmail), baa: baaLabel(o.baaStatus),
    bdate: s(o.baaSignedDate ?? (o.baaCountersignedAt ? o.baaCountersignedAt.slice(0, 10) : '')),
  };
}

/** Which fields each lens can store today (the rest are shown and left read only). */
const OPERATOR_SAVES = new Set(['logo', 'name', 'legal', 'plan', 'street', 'region', 'city', 'state', 'zip', 'npi', 'ccn', 'facid', 'tz', 'bname', 'bemail', 'baa', 'bdate']);
const FACILITY_SAVES = new Set(['beds', 'tz', 'own', 'units']);
const NOT_STORED = 'Not stored by the server yet, so it cannot be changed here.';

/** Organization (design seIsOrg, T8734; ORG and the save, L21817 and L22338-22370). */
export function OrganizationSection() {
  const { client } = useSession();
  const { lens, saved, changed } = usePage();
  const { canWrite, roReason } = useGuarded();
  const fa = lens.lens === 'Facility Admin';

  const status = useApi((c, s) => governedStatus(c, { signal: s }), [], !fa);
  const profile = useApi((c, s) => orgProfile(c, { signal: s }), [], !fa);
  const tenant = useApi((c, s) => governedSettings(c, { signal: s }), [], !fa);
  const facility = useApi((c, s) => facilityOrganization(c, { params: { id: 'me' }, signal: s }), [], fa);

  const [edits, setEdits] = useState<Record<string, string>>({});
  const [savedOnce, setSavedOnce] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const [logoOpen, setLogoOpen] = useState(false);
  const [outsideUS, setOutsideUS] = useState(false);
  const [zones] = useState(otherZones);

  const loading = fa ? !facility.data && !facility.error : !profile.data && !profile.error;
  const loadErr = fa ? facility.error : profile.error;
  if (loading) return <Skeleton rows={6} label="Loading the organization" />;
  if (loadErr && !(fa ? facility.data : profile.data)) return <LoadError error={loadErr} onRetry={fa ? facility.reload : profile.reload} />;

  const committed: Record<string, string> = fa
    ? fromFacility(facility.data!.organization)
    : fromProfile(profile.data!.profile, status.data?.tenant ?? null, (tenant.data?.settings as { region?: string } | undefined)?.region, profile.data!.sourced ?? null);
  const linked = fa ? !!facility.data!.organization.linkedFacilityId : !!profile.data!.sourced;
  const catalogKeys = fa ? FACILITY_CATALOG : OPERATOR_CATALOG;
  const saves = fa ? FACILITY_SAVES : OPERATOR_SAVES;
  const ov = (k: string) => (edits[k] !== undefined ? edits[k] : committed[k] ?? '');
  const lookupFirst = fa && !(committed.own && committed.units);
  const ORG = [...(lookupFirst ? [LOOKUP] : []), ...GROUPS_BEFORE, ...(lookupFirst ? [] : [LOOKUP]), ...GROUPS_AFTER];
  const dirtyKeys = Object.keys(edits).filter((k) => edits[k] !== committed[k]);
  const orgDirty = dirtyKeys.length;

  const setField = (k: string, v: string) => setEdits((p) => ({ ...p, [k]: v }));

  /** Send one set of values (a save, or an undo's earlier values). */
  const send = async (vals: Record<string, string>, reason?: string) => {
    if (fa) {
      const body: FacilityOrganizationBody = {};
      const num = (v: string) => (v.trim() === '' ? null : Number(v.replace(/[^0-9]/g, '')));
      if ('beds' in vals) body.licensedBeds = num(vals.beds);
      if ('units' in vals) body.units = num(vals.units);
      if ('tz' in vals) body.timezone = vals.tz.trim() || null;
      if ('own' in vals) body.ownership = vals.own.trim() || null;
      if (reason) body.reason = reason;
      await facilityOrganizationSave(client, { params: { id: 'me' }, body });
      facility.reload();
      return;
    }
    const p: Partial<OrgProfile> & { reason?: string } = {};
    const map: Record<string, keyof OrgProfile> = { legal: 'legalName', street: 'street', city: 'city', npi: 'npi', ccn: 'ccn', facid: 'facilityId', tz: 'timezone', logo: 'logoUrl', bname: 'billingContactName', bemail: 'billingContactEmail', bdate: 'baaSignedDate' };
    for (const [k, f] of Object.entries(map)) if (k in vals) (p as Record<string, string>)[f] = vals[k];
    if ('plan' in vals) p.plan = vals.plan === 'Not set' ? '' : vals.plan.toLowerCase();
    if ('baa' in vals) p.baaStatus = vals.baa.toLowerCase();
    if ('state' in vals) p.state = vals.state.trim();
    if ('zip' in vals) p.zip = vals.zip.trim();
    if (Object.keys(p).length) {
      if (reason) p.reason = reason;
      await orgProfileSave(client, { body: p });
    }
    if ('region' in vals && vals.region !== 'Not set') await tenantSettingsSave(client, { body: { region: vals.region } });
    if ('name' in vals && status.data?.tenant.id && vals.name.trim()) await tenantRename(client, { params: { id: status.data.tenant.id }, body: { name: vals.name.trim() } });
    profile.reload();
    tenant.reload();
    status.reload();
  };

  const save = async () => {
    if (!orgDirty || busy || !canWrite) return;
    const next: Record<string, string> = {};
    const prev: Record<string, string> = {};
    dirtyKeys.forEach((k) => {
      next[k] = edits[k];
      prev[k] = committed[k] ?? '';
    });
    setBusy(true);
    setErr(null);
    try {
      await send(next);
      setEdits({});
      setSavedOnce(true);
      changed();
      saved({ t: hhmm(new Date()), undo: () => send(prev, 'undo') });
    } catch (e) {
      setErr(e as ApiError);
    } finally {
      setBusy(false);
    }
  };

  const groups = ORG.map(([name, fields]) => ({
    name,
    fields: fields.map(([label, type, k, base]) => {
      const cur = ov(k);
      // A field the CMS catalog filled is shown read only: change the link to change it.
      const fromCatalog = linked && catalogKeys.has(k);
      const editable = canWrite && saves.has(k) && !fromCatalog && !(fa && k === 'baa');
      const why = roReason ?? (editable ? undefined : fromCatalog ? CATALOG_NOTE + '.' : fa && k === 'baa' ? 'Only Aithora records the BAA status.' : NOT_STORED);
      const hint = (fa ? facilityHint(k, facility.data!.organization) : operatorHint(k, linked, ov('facid'), dirtyKeys.includes('facid'))) || base;
      return { label, type, k, hint, cur, editable, fromCatalog, why, wrap: label === 'Linked facility id' || label === 'Organization id' ? 'grid-column:1 / -1;' : '' };
    }),
  }));

  return (
    <div data-testid="settings-organization" style={sx('box-sizing:border-box;padding:0 2px;min-width:0;')}>
      <div style={sx('display:flex;align-items:baseline;gap:6px 12px;flex-wrap:wrap;margin-bottom:12px;')}>
        <span style={sx('font-size:17px;font-weight:800;color:#22272E;')}>Organization</span>
        <span style={sx('font-size:16px;color:#54606C;')}>Profile for this governed organization: identity, address, billing and compliance.</span>
        {!fa && (
          // Restored from the Flutter console (no design control): the org logo, Aithora's
          // lens only. A text link on the heading line, so the form keeps the design's layout.
          <>
            <span style={sx('flex:1 1 10px;')} />
            <a href="#" aria-expanded={logoOpen} onClick={(e) => { pre(e); setLogoOpen(!logoOpen); }} style={sx('font-size:16px;font-weight:700;color:#2F5C97;text-decoration:none;white-space:nowrap;')}>
              {I(ov('logo') ? 'Logo set' : 'Logo URL')}
            </a>
          </>
        )}
      </div>
      {!fa && logoOpen && (
        <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin:-4px 0 12px;')}>
          <input
            type="text"
            aria-label="Logo URL"
            value={ov('logo')}
            readOnly={!canWrite}
            title={roReason ?? undefined}
            onChange={(e) => setField('logo', e.target.value)}
            placeholder="https://"
            style={sx('flex:1 1 320px;max-width:560px;height:36px;padding:0 12px;border:1px solid rgba(34,39,46,.14);border-radius:8px;background:#F7F3EA;font-size:16px;color:#22272E;font-family:inherit;box-sizing:border-box;')}
          />
          <span style={sx('font-size:16px;color:#54606C;')}>{I('https only · saved with Save changes')}</span>
        </div>
      )}
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,360px),1fr));gap:4px 28px;align-items:start;')}>
        {groups.map((g) => (
          <div key={g.name} style={sx('margin-bottom:14px;min-width:0;')}>
            <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-bottom:10px;')}>{I(g.name)}</div>
            <div style={sx('display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:12px 16px;')}>
              {g.fields.map((f) => (
                <Fragment key={f.k}>
                  <div style={sx(f.wrap)}>
                    <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-bottom:6px;')}>{I(f.label)}</div>
                    {f.type === 'text' && (
                      <input
                        type="text"
                        aria-label={f.label}
                        // A read-only signed date reads as the design writes it ("Aug 14, 2026").
                        value={!f.editable && f.k === 'bdate' ? longDate(f.cur) : f.cur}
                        readOnly={!f.editable}
                        title={f.why}
                        onChange={(e) => setField(f.k, e.target.value)}
                        placeholder="Not set"
                        style={sx(FIELD + (f.fromCatalog ? FILLED : ''))}
                      />
                    )}
                    {f.type === 'copy' && (
                      // The full id, wrapped onto more lines when it is long (never cut short), with
                      // Copy as its own button beside the field.
                      <div style={sx('display:flex;align-items:flex-start;gap:8px;min-width:0;')}>
                        <span
                          data-testid="org-id"
                          style={sx('flex:1 1 auto;min-width:0;box-sizing:border-box;min-height:36px;padding:9px 12px;border:1px solid rgba(34,39,46,.14);border-radius:8px;background:#F7F3EA;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-size:16px;line-height:1.4;color:#22272E;overflow-wrap:anywhere;word-break:break-all;')}
                        >
                          {f.cur || I('Not set')}
                        </span>
                        <button
                          type="button"
                          disabled={!f.cur}
                          onClick={() => {
                            void navigator.clipboard?.writeText(f.cur).catch(() => undefined);
                            setCopied(true);
                          }}
                          style={sx('flex:0 0 auto;display:inline-flex;align-items:center;height:36px;padding:0 14px;border-radius:8px;background:#FCFAF4;border:1px solid rgba(34,39,46,.16);font-family:inherit;font-size:16px;font-weight:700;color:#3C454F;cursor:pointer;white-space:nowrap;')}
                        >
                          {I(copied ? 'Copied' : 'Copy')}
                        </button>
                      </div>
                    )}
                    {f.type === 'tz' && (
                      <select
                        aria-label={f.label}
                        value={f.cur}
                        disabled={!f.editable}
                        title={f.why}
                        onChange={(e) => setField(f.k, e.target.value)}
                        style={sx(FIELD + 'padding:0 8px;')}
                      >
                        <option value="">Not set</option>
                        {f.cur && !US_ZONES.some(([z]) => z === f.cur) && !zones.includes(f.cur) && <option value={f.cur}>{f.cur}</option>}
                        <optgroup label="United States">
                          {US_ZONES.map(([z, name]) => (
                            <option key={z} value={z}>{name + ' · ' + z}</option>
                          ))}
                        </optgroup>
                        <optgroup label="All time zones">
                          {zones.map((z) => (
                            <option key={z} value={z}>{z}</option>
                          ))}
                        </optgroup>
                      </select>
                    )}
                    {f.type === 'state' && (() => {
                      const code = f.cur.trim().toUpperCase();
                      const abroad = outsideUS || (!!f.cur.trim() && !US_STATE_CODES.has(code));
                      return (
                        <>
                          <select
                            aria-label={f.label}
                            value={abroad ? OUTSIDE : US_STATE_CODES.has(code) ? code : ''}
                            disabled={!f.editable}
                            title={f.why}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === OUTSIDE) {
                                setOutsideUS(true);
                                if (US_STATE_CODES.has(code)) setField(f.k, '');
                              } else {
                                setOutsideUS(false);
                                setField(f.k, v);
                              }
                            }}
                            style={sx(FIELD + 'padding:0 8px;' + (f.fromCatalog ? FILLED : ''))}
                          >
                            <option value="">Not set</option>
                            {US_STATES.map(([c, name]) => (
                              <option key={c} value={c}>{name}</option>
                            ))}
                            <option value={OUTSIDE}>Outside the US</option>
                          </select>
                          {abroad && (
                            <input
                              type="text"
                              aria-label="State, province or region outside the US"
                              value={f.cur}
                              readOnly={!f.editable}
                              title={f.why}
                              onChange={(e) => setField(f.k, e.target.value)}
                              placeholder="Province or region"
                              style={sx(FIELD + 'margin-top:6px;' + (f.fromCatalog ? FILLED : ''))}
                            />
                          )}
                        </>
                      );
                    })()}
                    {f.type === 'select' && (
                      <a
                        href="#"
                        role="button"
                        aria-label={`${f.label}: ${f.cur}`}
                        aria-disabled={!f.editable || undefined}
                        title={f.why}
                        onClick={(e) => {
                          pre(e);
                          if (!f.editable) return;
                          const opts = CYCLES[f.k];
                          setField(f.k, opts[(opts.indexOf(f.cur) + 1) % opts.length]);
                        }}
                        style={sx('display:flex;align-items:center;height:36px;padding:0 12px;border:1px solid rgba(34,39,46,.14);border-radius:8px;background:#F7F3EA;font-size:16px;color:#22272E;text-decoration:none;')}
                      >
                        {I(f.cur)}
                        <span style={sx('flex:1;')} />
                        <span style={sx('color:#54606C;')}>{'▾'}</span>
                      </a>
                    )}
                    {f.hint && <div style={sx('font-size:16px;color:#54606C;margin-top:5px;')}>{I(f.hint)}</div>}
                  </div>
                </Fragment>
              ))}
            </div>
          </div>
        ))}
      </div>
      {/* The building's contact number and its text consent (AF-1566): the Pulse downtime
          fallback texts it only with consent on file. A building's own Facility Admin sees it;
          Aithora's lens has no building here (the governance tenant id is not a building, and
          asking for it answered 404 "Building not found" on every Settings visit). */}
      <BuildingContactConsent orgId={fa ? 'me' : null} />
      <div style={sx('position:sticky;bottom:0;background:#F5F1E8;display:flex;align-items:center;gap:12px;flex-wrap:wrap;row-gap:6px;padding:10px 0 4px;border-top:1px solid rgba(34,39,46,.1);')}>
        <span data-testid="org-dirty" style={sx('font-size:16px;' + (orgDirty ? 'color:#22272E;font-weight:700;' : 'color:#54606C;'))}>
          {I(orgDirty === 0 ? 'No unsaved changes' : orgDirty === 1 ? '1 unsaved change' : orgDirty + ' unsaved changes')}
        </span>
        {orgDirty > 0 && (
          <a
            href="#"
            onClick={(e) => {
              pre(e);
              setEdits({});
            }}
            style={sx('font-size:16px;font-weight:700;color:#54606C;text-decoration:none;')}
          >
            Discard
          </a>
        )}
        <span style={sx('flex:1;')} />
        <a
          href="#"
          role="button"
          aria-disabled={!orgDirty || !canWrite || busy || undefined}
          title={roReason ?? undefined}
          onClick={(e) => {
            pre(e);
            void save();
          }}
          style={sx(btn(orgDirty ? 'primary' : 'ghost') + (orgDirty ? '' : 'opacity:.6;'))}
        >
          {I(savedOnce && !orgDirty ? 'Saved' : 'Save changes')}
        </a>
      </div>
      <ErrLine error={err} />
    </div>
  );
}
